/**
 * A product's options — the colours, sizes or storage tiers a shopper picks
 * between — and what a cart line needs from them.
 *
 * An option's id is the key its stock sits under. A cart line without one
 * sells at the base price, and checkout takes the stock of whichever option
 * is oldest (convex/helpers/stockAllocation.ts). That is how the website sold
 * Mutoni's power banks with no colour (Oct–Nov 2026): every electronics
 * product drew the fashion card, and its Buy added the product with none.
 *
 * The options are the product's saved variants — `variants[]`, else
 * `fashionAttributes.variants[]` — the list the price resolver reads
 * (lib/utils/variantPricing.ts). Pure: the website's cards and
 * convex/cart/core.ts both use it.
 */

import { isFoodProduct } from "./food";

/** The product's saved options, in order — only those with an id. */
export function productOptions(product: any): any[] {
  const own =
    Array.isArray(product?.variants) && product.variants.length ? product.variants : product?.fashionAttributes?.variants;
  return Array.isArray(own) ? own.filter((v: any) => v && typeof v.id === "string" && v.id.trim()) : [];
}

/** What a cart line shows for its option — the website's `selectedVariants`. */
export interface OptionLabels {
  size?: string;
  color?: string;
  colorHex?: string;
  isMulticolor?: boolean;
  multicolorList?: string[];
}

const text = (value: unknown): string | undefined => {
  const s = typeof value === "string" ? value.trim() : "";
  return s || undefined;
};

/** A colour or size as a shopper reads it: an option with no size is stored as "default". */
const named = (value: unknown): string | undefined => {
  const s = text(value);
  return s && s.toLowerCase() !== "default" ? s : undefined;
};

/**
 * The colour and size of one option. A fashion option carries them itself
 * (colorName, colorHex, size); a product option keeps them under
 * `attributes`, and its swatch on the product's own colour list.
 */
export function optionLabels(product: any, option: any): OptionLabels {
  const attrs = option?.attributes || {};
  const color = named(option?.colorName ?? attrs.color);
  const size = named(option?.size ?? attrs.size);
  const colours: any[] = [
    ...(product?.electronicsAttributes?.colors || []),
    ...(product?.fashionAttributes?.colors || []),
  ];
  const colour =
    (option?.colorId && colours.find((c) => c?.id === option.colorId)) ||
    (color && colours.find((c) => c?.name === color)) ||
    undefined;
  const colorHex = text(option?.colorHex) || text(colour?.hex);
  const multicolorList = option?.multicolorList ?? attrs.multicolorList ?? colour?.multicolorList;
  const isMulticolor = (option?.isMulticolor ?? attrs.isMulticolor ?? colour?.isMulticolor) === true;
  return {
    ...(size ? { size } : {}),
    ...(color ? { color } : {}),
    ...(color && colorHex ? { colorHex } : {}),
    ...(color && isMulticolor
      ? { isMulticolor: true, ...(Array.isArray(multicolorList) && multicolorList.length ? { multicolorList } : {}) }
      : {}),
  };
}

/** What a shop card's Buy does — see buyActionFor. */
export type BuyAction =
  | { kind: "add" }
  | { kind: "option"; variantId: string; selectedVariants?: OptionLabels }
  | { kind: "pick" }
  | { kind: "page" };

/** How many combinations a picker offers for its axes (colours, sizes…): 0 when it has none. */
export function choicesOf(...axes: number[]): number {
  return axes.some((n) => n > 0) ? axes.reduce((total, n) => total * Math.max(n, 1), 1) : 0;
}

/**
 * What a shop card's Buy does. `choices` is how many combinations the card's
 * own picker offers (choicesOf its colours, sizes or storage tiers).
 *
 *   - pick:   the picker has a choice to offer;
 *   - page:   the product has options the card cannot show, so its page shows them;
 *   - option: one option and nothing to choose, so it is added as that option;
 *   - add:    a plain product, added as it is.
 *
 * A product whose options were never saved keeps its picker for the one
 * combination it shows: the picker names that one, as it always has.
 */
export function buyActionFor(product: any, choices: number): BuyAction {
  if (choices > 1) return { kind: "pick" };
  const options = productOptions(product);
  if (options.length > 1) return { kind: "page" };
  if (options.length === 1) {
    const labels = optionLabels(product, options[0]);
    return {
      kind: "option",
      variantId: String(options[0].id),
      ...(Object.keys(labels).length ? { selectedVariants: labels } : {}),
    };
  }
  if (choices === 1) return { kind: "pick" };
  // Flagged with options the row did not bring along (one built from the lean mirror).
  if (product?.hasVariants === true) return { kind: "page" };
  return { kind: "add" };
}

/**
 * Why an add-to-cart must be refused, or null when it may go ahead.
 *
 * A product sold in two or more options needs the one chosen. It is refused
 * only when the request names no choice at all — no option id, and no colour
 * or size. The Kuttos app's fashion pages send the colour and size by name
 * with no id (mobile/components/VariantOptions.tsx) and must keep working. A
 * menu item is chosen by its portion's name (lib/config/food.ts), never by id.
 */
export function missingOptionMessage(
  product: any,
  request: {
    variantId?: string | null;
    selectedVariants?: { size?: string | null; color?: string | null } | null;
  },
): string | null {
  if (!product || isFoodProduct(product)) return null;
  if (text(request.variantId)) return null;
  if (text(request.selectedVariants?.size) || text(request.selectedVariants?.color)) return null;
  if (productOptions(product).length < 2) return null;
  return `${product.name} comes in more than one option. Open the product and choose the one you want.`;
}
